import { useMemo } from 'react';

import { resolveActiveLocale } from './useActiveLocale.ts';

/**
 * Plant-timezone resolution + timezone-aware date formatting.
 *
 * Manufacturing data is timestamped in UTC on the wire, but an operator on the
 * floor reads shift boundaries, batch start/stop, and alarm times in the
 * *plant's* local timezone — which is NOT necessarily the browser's timezone
 * (a supervisor reviewing a Munich line from a Chicago laptop must still see
 * Munich wall-clock times). Formatting a date with the browser default silently
 * shifts every timestamp by the operator's own offset.
 *
 * This module exposes:
 *   - {@link usePlantTimeZone} — the active plant's IANA timezone string.
 *   - {@link formatInPlantTz} — pure `Intl`-based formatter pinned to a tz.
 *   - {@link usePlantDateFormat} — a memoized formatter bound to the active
 *     plant tz (the hook most view code should reach for).
 *
 * Resolution order for the active plant timezone:
 *   1. An app-registered resolver (see {@link registerPlantTimeZoneResolver}) —
 *      the shell wires this from its plant/site context.
 *   2. The browser's resolved timezone (`Intl…resolvedOptions().timeZone`).
 *   3. `'UTC'` (SSR / test / locked-down runtimes with no Intl tz data).
 *
 * The registration seam mirrors the list-archetype identity/permission hooks:
 * `@tyndo/hooks` stays free of any plant-context dependency, and callers that
 * never register a resolver still get correct (browser-local) behaviour.
 */

/** Zero-arg resolver returning the active plant's IANA timezone, or null. */
export type PlantTimeZoneResolver = () => string | null | undefined;

let _resolver: PlantTimeZoneResolver | null = null;

/**
 * Register the app's active-plant timezone resolver. The shell calls this once
 * (e.g. from its plant/site provider) so every list/detail view formats
 * timestamps in plant-local time without taking a direct dependency on the
 * plant context. Passing `null` clears the registration (used in tests).
 */
export function registerPlantTimeZoneResolver(fn: PlantTimeZoneResolver | null): void {
  _resolver = fn;
}

/** Browser-resolved IANA timezone, or `'UTC'` when unavailable. */
function browserTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Resolve the active plant's IANA timezone string, outside React. Pure —
 * usable from imperative/formatter paths that can't run a hook.
 */
export function resolvePlantTimeZone(): string {
  const fromApp = _resolver?.();
  if (fromApp) return fromApp;
  return browserTimeZone();
}

/**
 * The active plant's IANA timezone (e.g. `'Europe/Berlin'`). Falls back to the
 * browser timezone, then `'UTC'`. Stable across renders unless the registered
 * resolver's value changes between renders (it is read each call, so a plant
 * switch that re-renders the tree picks up the new zone).
 */
export function usePlantTimeZone(): string {
  return resolvePlantTimeZone();
}

/**
 * Pure timezone-pinned date/time formatter. Formats `value` (Date | ms-epoch |
 * ISO string) in `timeZone` using the given locale + `Intl` options. Returns an
 * empty string for nullish/invalid input so a table cell renders blank rather
 * than "Invalid Date".
 *
 * `locale` defaults to the RESOLVED ACTIVE LOCALE, not to `undefined`. Passing
 * `undefined` through to `Intl` pins the format to whatever locale the
 * operator's OS is set to, so the timezone would be correctly plant-pinned
 * while the date order silently flipped between `05.08.2026` and `8/5/2026`
 * for two people reviewing the same batch record.
 */
export function formatInPlantTz(
  value: Date | number | string | null | undefined,
  timeZone: string,
  options?: Intl.DateTimeFormatOptions,
  locale?: string,
): string {
  if (value == null || value === '') return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const loc = locale ?? resolveActiveLocale();
  try {
    return new Intl.DateTimeFormat(loc, { ...options, timeZone }).format(date);
  } catch {
    // Invalid tz (bad registered value) — degrade to browser-local rather than
    // throwing through a render.
    return new Intl.DateTimeFormat(loc, options).format(date);
  }
}

/**
 * A memoized formatter bound to the active plant timezone. This is the hook
 * most view code should use to render timestamps: `fmt(row.startedAt)` yields
 * the plant-local wall-clock string. `options` defaults to a compact
 * date+time; pass your own `Intl.DateTimeFormatOptions` to override.
 *
 * ```tsx
 * const fmtPlant = usePlantDateFormat({ dateStyle: 'medium', timeStyle: 'short' });
 * <td>{fmtPlant(row.startedAt)}</td>
 * ```
 */
export function usePlantDateFormat(
  options?: Intl.DateTimeFormatOptions,
  locale?: string,
): (value: Date | number | string | null | undefined) => string {
  const timeZone = usePlantTimeZone();
  return useMemo(
    () =>
      (value: Date | number | string | null | undefined) =>
        formatInPlantTz(value, timeZone, options, locale),
    [timeZone, options, locale],
  );
}
